import { Button } from "primereact/button"
import OrderFormGoodsItem from "./GoodsItem"
import { v4 as uuidv4 } from "uuid"
import { SyntheticEvent, useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { OrderParams } from "."
import { useUpdateGoods } from "../../../api/queries/useUpdateGoods"
import { OrderGoods, OrderGoodsADR, UpdateOrderGoodsPaylod, UpdateOrderGoodsResponseData } from "../../../api/types/order"
import { validateGoods } from "./validations/goods"
import { useOrderDetail } from "../../../api/queries/useOrderDetail"
import { FormErrorsArr } from "../../FieldError"
import { isOrderEditable } from "../StateTag"
import { usePrompt } from "../../../common/usePrompt"
import styles from "./styles.module.css"
import { scrollToFirstInvalidElement } from "../../../common/util"

export default function OrderFormGoods() {
  const navigate = useNavigate()

  const { uuid } = useParams<OrderParams>()
  const { data: orderDetail } = useOrderDetail(uuid)
  const updateGoods = useUpdateGoods(uuid)

  useEffect(() => {
    if (orderDetail) {
      const { goods } = orderDetail.results.orderDetail

      if (goods) {
        setItems(goods)
      }

      setIsInited(true)
    }
  }, [orderDetail])

  const [items, setItems] = useState<OrderGoods[]>([
    Object.assign({}, defaultGoods, { uuid: uuidv4() })
  ])

  const [errors, setErrors] = useState<FormErrorsArr<OrderGoods>>([])
  const [goodsADRErrors, setGoodsADRErrors] = useState<FormErrorsArr<OrderGoodsADR>[]>([])

  const handleAddItemClick = (): void => {
    setItems([...items, Object.assign({}, defaultGoods, { uuid: uuidv4() })])
  }

  const handleGoodsItemChange = (good: OrderGoods, index: number): void => {
    let newItems = [...items]
    newItems[index] = good
    setItems(newItems)

    // reset good errors on change
    let obj: { [key: string]: string } = {}
    const requiredFields = ["content", "pieces", "weight", "packing", "length", "width", "height"]
    requiredFields.forEach((fieldName: string) => {
      if (good[fieldName as keyof OrderGoods]) {
        obj[fieldName] = ""
      }
    })
    setErrors(errors.map((err, i) => index === i ? Object.assign({}, err, obj) : err))

    // reset good adr errors on change
    if (goodsADRErrors.length > index) {
      let newAdrErrors = [...goodsADRErrors[index]]
      const adrRequiredFields = ["codeUN", "pieces", "brutto", "netto", "technicalName"]
      good.adr.forEach((adr, i) => {
        adrRequiredFields.forEach((fieldName: string) => {
          if (adr[fieldName as keyof OrderGoodsADR]) {
            newAdrErrors[i][fieldName as keyof OrderGoodsADR] = ""
          }
        })
      })
      setGoodsADRErrors(goodsADRErrors.map((err, i) => index === i ? newAdrErrors : err))
    }
  }

  const handleRemoveItem = (itemIndex: number): void => {
    if (window.confirm(`Opravdu chcete smazat položku č. ${itemIndex + 1}?`)) {
      setItems(items.filter((_: OrderGoods, index: number) => index !== itemIndex))
    }
  }

  const handleRemoveADRItem = (itemIndex: number, ADRIndex: number): void => {
    const newItems = [...items]
    newItems[itemIndex].adr = newItems[itemIndex].adr
      .filter((_: OrderGoodsADR, index: number) => ADRIndex !== index)
    setItems(newItems)
  }

  const handleBackClick = (): void => {
    navigate(`/orders/${uuid}/unloading`)
  }

  const handleFormSubmit = async (event: SyntheticEvent) => {
    event.preventDefault()

    if (!orderDetail) {
      return
    }

    const payload: UpdateOrderGoodsPaylod = {
      goods: items,
    }

    const [isValid, errors, goodsADRErrors] = validateGoods(payload, orderDetail.results.orderDetail)
    setErrors(errors)
    setGoodsADRErrors(goodsADRErrors)

    if (isValid) {
      await updateGoods.mutateAsync(payload)
        .then(() => {
          setChangeCounter(0)
          setNextStep(true)
        })
        .catch(({ response }) => {
          let { data }: { data: UpdateOrderGoodsResponseData } = response
          let respErrors: FormErrorsArr<OrderGoods> = []

          items.forEach(() => {
            respErrors.push({})
          })

          data.errors.forEach((error) => {
            if (typeof error.property === "string") {
              respErrors[error.entityId][error.property] = error.message
            }
          })

          setErrors(respErrors)
          scrollToFirstInvalidElement()
        })
    }
  }
  const [isInited, setIsInited] = useState<boolean>(false)
  const [changeCounter, setChangeCounter] = useState<number>(0)
  const [nextStep, setNextStep] = useState<boolean>(false)

  // prompt about unsaved form changes
  useEffect(() => {
    if (isInited) {
      setChangeCounter(i => i + 1)
    }
  }, [isInited, items])

  usePrompt("Máte neuložené změny. Opravdu chcete odejít?", changeCounter > 1)

  // redirect to next step
  useEffect(() => {
    if (changeCounter <= 1 && nextStep) {
      navigate(`/orders/${uuid}/calculation`)
    }
  }, [changeCounter, nextStep, navigate, uuid])

  const isEditable = isOrderEditable(orderDetail?.results.orderDetail.state || 0)

  return (
    <form className={`col-12 ${isEditable ? "" : "p-disabled"}`} onSubmit={handleFormSubmit}>
      <div className={"p-fluid"}>
        {items.map((item, i) => (
          <OrderFormGoodsItem
            key={i}
            index={i}
            item={item}
            goodsItemChangeHandler={handleGoodsItemChange}
            goodsItemRemoveHandler={handleRemoveItem}
            goodsADRItemRemoveHandler={handleRemoveADRItem}
            canRemove={items.length > 1}
            errors={errors[i]}
            ADRErrors={goodsADRErrors[i]}
            isLast={i === items.length - 1}
          />
        ))}

        <div className={"mb-6 text-center"}>
          {items.length < MAX_GOODS_COUNT && (
            <Button onClick={handleAddItemClick} type={"button"}
              className={`p-button-lg p-button-icon-only ${styles.addNewButtonBlue}`}
              icon={"pi pi-plus"} title={"Přidat položku"} />
          )}
        </div>
      </div>

      {isEditable && (
        <div className={`${styles.controls} md:flex`}>
          <div className={"flex-1"}>
            <Button type={"button"} label={"Předchozí krok"}
              className={"p-button p-button-sm p-button-secondary mr-1"}
              icon={"pi pi-arrow-circle-left"}
              disabled={updateGoods.isLoading} onClick={handleBackClick} />
          </div>

          <div className={"flex-1 text-right"}>
            <Button type={"submit"} label={"Další krok"}
              className={"p-button p-button-sm p-button-success"}
              icon={"pi pi-arrow-circle-right"} iconPos={"right"}
              disabled={updateGoods.isLoading} />
          </div>
        </div>
      )}
    </form>
  )
}

const defaultGoods: OrderGoods = {
  content: "",
  pieces: 0,
  weight: 0,
  packing: "",
  signification: "",
  length: 0,
  width: 0,
  height: 0,
  adr: []
}

export const MAX_GOODS_COUNT = 10;
